import { Component, inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { lastValueFrom } from 'rxjs';

import { ProviderRegistration, ContactTypes } from '../../models/provider-registration.model';
import { ProviderRegistrationService } from '../../services/provider-registration.service';

@Component({
  selector: 'app-provider-registration-dialog',
  templateUrl: './provider-registration-dialog.component.html',
  styleUrls: ['./provider-registration-dialog.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule
  ]
})
export class ProviderRegistrationDialogComponent {
  private fb = inject(FormBuilder);
  private dialogRef = inject(MatDialogRef<ProviderRegistrationDialogComponent>);
  private providerService = inject(ProviderRegistrationService);

  showSuccessMessage = false;
  isSubmitting = false;
  errorMessage = '';

  contactTypes = [
    { value: ContactTypes.OWNER, label: 'Propietario' },
    { value: ContactTypes.OPERATIONS, label: 'Operaciones' }
  ];

  providerForm: FormGroup = this.fb.group({
    commercialName: ['', [Validators.required, Validators.minLength(2)]],
    rfc: ['', [Validators.required, Validators.pattern(/^[A-ZÑ&]{3,4}[0-9]{6}[A-Z0-9]{3}$/)]],
    providerNumber: ['', [Validators.required]],
    contacts: this.fb.array([
      this.createContactFormGroup()
    ])
  });

  get contacts(): FormArray {
    return this.providerForm.get('contacts') as FormArray;
  }

  createContactFormGroup(): FormGroup {
    return this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
      type: ['', Validators.required]
    });
  }

  addContact(): void {
    this.contacts.push(this.createContactFormGroup());
  }

  removeContact(index: number): void {
    if (this.contacts.length > 1) {
      this.contacts.removeAt(index);
    }
  }

  async onSubmit(): Promise<void> {
    if (this.providerForm.valid) {
      this.isSubmitting = true;
      this.errorMessage = '';
      
      try {
        const formValue = this.providerForm.value as ProviderRegistration;
        // Set active to true by default since the checkbox was removed
        formValue.active = true;
        
        // Send the data to the webhook
        const response = await lastValueFrom(this.providerService.registerProvider(formValue));
        
        console.log('Provider registration response:', response);
        
        // Show success message
        this.showSuccessMessage = true;
      } catch (error) {
        console.error('Error registering provider:', error);
        this.errorMessage = 'Ocurrió un error al registrar el proveedor. Por favor intente nuevamente.';
      } finally {
        this.isSubmitting = false;
      }
    } else {
      // Mark all fields as touched to show validation errors
      this.markFormGroupTouched(this.providerForm);
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else if (control instanceof FormArray) {
        control.controls.forEach(arrayControl => {
          if (arrayControl instanceof FormGroup) {
            this.markFormGroupTouched(arrayControl);
          }
        });
      }
    });
  }

  getErrorMessage(controlName: string, contactIndex?: number): string {
    let control;
    
    if (contactIndex !== undefined) {
      control = this.contacts.at(contactIndex).get(controlName);
    } else {
      control = this.providerForm.get(controlName);
    }

    if (control?.hasError('required')) {
      return 'Este campo es obligatorio';
    }
    
    if (control?.hasError('email')) {
      return 'Ingrese un correo válido';
    }
    
    if (control?.hasError('pattern')) {
      switch (controlName) {
        case 'rfc':
          return 'RFC inválido (ej: ABC123456XYZ)';
        case 'phone':
          return 'Teléfono debe tener 10 dígitos';
        case 'providerNumber':
          return 'Solo números permitidos';
        default:
          return 'Formato inválido';
      }
    }
    
    if (control?.hasError('minlength')) {
      return 'Mínimo 2 caracteres';
    }
    
    return '';
  }
}